import {env} from './env';

/**
 * Tenant IDs for special-purpose demo tenants.
 * These tenants show only their domain's features in the sidebar.
 */
const RELAY_DEMO_TENANT = '12345678-1234-5678-9abc-11111111111c';
const TECHCOMM_TENANT = 'b2cfe3d2-7edb-498e-8bcb-00d6d0cf004f';
const NEW_WAVE_DESIGN_TENANT = 'c6fc8056-6a2d-4d55-ac53-bc5cf8ea4f5c';
const AES_TENANT = '38b85355-6011-4791-aeb8-8ac498970422';
const DATA_METALCRAFT_TENANT = 'e407c4d0-77b3-4a32-ab52-933001f82d78';

/** Prod tenants with all features enabled. */
const FULL_ACCESS_TENANTS = new Set([
  'e11962ea-c9ad-4465-b4ce-64bebdfd4ca4', // Bemis
  '78b9eb7f-7ba4-4396-a0a8-f10f1ae09fc0', // Chromalloy
  '29ed727a-c6ce-49a3-894a-35319c07ad83', // Internal Materials
  NEW_WAVE_DESIGN_TENANT, // New Wave Design
  AES_TENANT, // Advanced Exhaust Solutions
  DATA_METALCRAFT_TENANT, // Data Metalcraft
]);

/** True for dev tenants (12345678-* UUIDs) and E2E test tenants (e2e00000-*). */
export const isDevTenant = (tenantId: string) =>
  tenantId.startsWith('12345678') || tenantId.startsWith('e2e00000');

/**
 * Teams ingestion admin UI (Relay config → Connections, plus the Teams pieces
 * of the shared Microsoft Entra registration card). Tenant-gated rather than
 * build-gated so prod can enable Teams for a specific set of tenants without an
 * env-wide flip — mirrors `canUseRelayView`. Covers every dev tenant
 * (`12345678-*`) and E2E/prod test tenant (`e2e00000-*`) via `isDevTenant`,
 * plus the TechComm prod tenant.
 */
export const isTeamsIntegrationEnabled = (tenantId: string): boolean =>
  isDevTenant(tenantId) ||
  tenantId === TECHCOMM_TENANT ||
  tenantId === AES_TENANT;

/**
 * Cetec ERP integration admin UI (Relay config → Connections). Enable it for
 * every development and E2E test tenant, plus TechComm for production testing.
 * Keep the gate tenant-scoped so it is not exposed to every production tenant.
 */
export const isCetecIntegrationEnabled = (tenantId: string): boolean =>
  isDevTenant(tenantId) || tenantId === TECHCOMM_TENANT;

/**
 * Relay demo exclusion only applies in the development environment, where
 * this UUID maps to RevStar. In local-cloud the same UUID is the chromalloy
 * demo tenant, which should still get scout/lattice/etc.
 */
const isRelayDemo = (tenantId: string) =>
  env.APP_ENV === 'development' && tenantId === RELAY_DEMO_TENANT;

/**
 * Relay view (the streamlined Library + Triage `uiMode`) availability.
 * Dev tenants get it for testing; TechComm is the first prod tenant opted in.
 * Gates both the QMS↔Relay sidebar toggle and the Relay-mode landing redirect.
 */
export const canUseRelayView = (tenantId: string) =>
  isDevTenant(tenantId) ||
  tenantId === TECHCOMM_TENANT ||
  tenantId === NEW_WAVE_DESIGN_TENANT ||
  tenantId === AES_TENANT ||
  tenantId === DATA_METALCRAFT_TENANT;

/**
 * Static feature flags — NOT tenant-scoped.
 * Prefer tenantFeatures when tenantId is available.
 */
export const features = {
  /** Otto's generated suggestions on Home and the Library chat landing. Off by default: each
   *  visit costs an LLM call. The static suggestion rows on Home stay. */
  homeRecommendations: env.HOME_RECOMMENDATIONS_ENABLED,
  relay: env.APP_ENV !== 'prod',
  mdm: env.APP_ENV !== 'prod',
  scheduling: env.APP_ENV !== 'prod',
} as const;

/** Tenant-scoped feature flags — require tenantId at call site. */
export const tenantFeatures = {
  /** Lattice — dev tenants, full-access, or TechComm. Excluding relay demo (RevStar in dev). */
  lattice: (tenantId: string) =>
    !isRelayDemo(tenantId) &&
    (isDevTenant(tenantId) ||
      FULL_ACCESS_TENANTS.has(tenantId) ||
      tenantId === TECHCOMM_TENANT),
  /** Scout — hidden on relay demo tenant (RevStar in dev shouldn't see AI features). */
  scout: (tenantId: string) => !isRelayDemo(tenantId),
  /** Relay — non-prod, or any tenant opted into the relay view (dev tenants,
   *  TechComm, New Wave). Kept in lockstep with canUseRelayView so the relay
   *  nav items + relay-config Settings link match the relay-view opt-in; they
   *  previously diverged and hid the config tab from prod relay tenants. */
  relay: (tenantId: string) =>
    env.APP_ENV !== 'prod' || canUseRelayView(tenantId),
  /** MDM — non-prod, dev tenants, full-access, or TechComm. */
  mdm: (tenantId: string) =>
    env.APP_ENV !== 'prod' ||
    isDevTenant(tenantId) ||
    FULL_ACCESS_TENANTS.has(tenantId) ||
    tenantId === TECHCOMM_TENANT,
  /** Scheduling — non-prod, dev tenants, or full-access. */
  scheduling: (tenantId: string) =>
    env.APP_ENV !== 'prod' ||
    isDevTenant(tenantId) ||
    FULL_ACCESS_TENANTS.has(tenantId),
  /** Supplier Quality — non-prod, dev tenants, or full-access. */
  supplierQuality: (tenantId: string) =>
    env.APP_ENV !== 'prod' ||
    isDevTenant(tenantId) ||
    FULL_ACCESS_TENANTS.has(tenantId),
  /** Scribe — non-prod, dev tenants, TechComm, or full-access. */
  scribe: (tenantId: string) =>
    env.APP_ENV !== 'prod' ||
    isDevTenant(tenantId) ||
    tenantId === TECHCOMM_TENANT ||
    FULL_ACCESS_TENANTS.has(tenantId),
  /** Otto — non-prod, dev tenants, TechComm, or AES. */
  otto: (tenantId: string) =>
    env.APP_ENV !== 'prod' ||
    isDevTenant(tenantId) ||
    tenantId === TECHCOMM_TENANT ||
    tenantId === AES_TENANT,
} as const;
